import { User } from '@remarkable/ark-icons';
import { Divider, Typography } from '@remarkable/ark-web';
import { useState } from 'react';
import { type FleetUser } from 'src/api/endpoints/cloudEnterprise.types';
import { Button, Input, Modal, NotificationBox } from 'src/components';

type TransferOwnershipModalProps = Readonly<{
  member: FleetUser;
  closeModal: () => void;
  handleTransfer: () => Promise<void>;
}>;

export function TransferOwnershipModal({
  member,
  closeModal,
  handleTransfer,
}: TransferOwnershipModalProps) {
  const [confirmInput, setConfirmInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const isConfirmed = confirmInput.toLowerCase() === 'transfer';

  const handleConfirmClick = async () => {
    setTransferError(null);
    setIsSubmitting(true);

    try {
      await handleTransfer();
      setConfirmInput('');
      closeModal();
    } catch {
      setTransferError('Unable to transfer ownership. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal.Content title="Transfer ownership?">
      <div className="flex flex-col gap-y-24">
        <Typography
          variant="body-lg"
          as="div"
          className="flex flex-wrap items-center justify-center gap-4"
        >
          <User size={24} className="mr-4 inline" />
          <b>{member.name}</b> <span>({member.email})</span>
        </Typography>
        <Divider />
        <Typography variant="body-md" as="div" className="text-left">
          Here&apos;s what will happen:
          <ul className="mt-20 ml-16 flex list-outside list-disc flex-col items-start text-left">
            <li>
              The selected user will be granted the <b>Owner</b> role
            </li>
            <li>
              They will have full access to manage users, devices, settings, and
              billing
            </li>
            <li>
              You will lose your Owner role and be demoted to <b>Admin</b>
            </li>
            <li>Only one owner can exist in the organization at a time</li>
          </ul>
        </Typography>
        <Input
          label={
            <Typography variant="body-md" className="text-left">
              Please type <b>Transfer</b> to confirm
            </Typography>
          }
          value={confirmInput}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setConfirmInput(e.currentTarget.value);
            if (transferError) {
              setTransferError(null);
            }
          }}
          name="confirm-transfer-ownership"
          data-cy="confirm-transfer-ownership"
          className="w-full"
          required
          maxLength={150}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          autoComplete="off"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              if (isConfirmed) {
                void handleConfirmClick();
              }
            }
          }}
        />

        {transferError && (
          <NotificationBox variant="error" title="Transfer failed">
            <Typography variant="body-sm">{transferError}</Typography>
          </NotificationBox>
        )}

        <div className="flex justify-center gap-16">
          <Modal.Close asChild>
            <Button variant="secondary" disabled={isSubmitting}>
              Cancel
            </Button>
          </Modal.Close>

          <Button
            variant="primary"
            loading={isSubmitting}
            disabled={!isConfirmed || isSubmitting}
            onClick={() => {
              void handleConfirmClick();
            }}
          >
            Confirm
          </Button>
        </div>
      </div>
    </Modal.Content>
  );
}
